export const FILE_UPLOAD_BUCKET_NAMES = [
    'ppl-ai-file-upload',
    'ppl-ai-sandbox-file-upload',
    'ppl-ai-experimental-file-upload',
];
// Keep in sync with BE (web/server/rest_async/rest/collection/models.py)
export const normalizeUrl = (url) => {
    return url
        .trim() // Remove whitespace
        .replace(/\/+$/, '') // Remove trailing slashes
        .replace(/^(?:https?:\/\/)?(?:www\.)?/, ''); // Remove protocol and www.
};
// Regex taken and modified from
// https://www.bomberbot.com/javascript/how-to-validate-urls-in-javascript-the-ultimate-guide/
// Covers most cases but not all domain names
// Keep in sync with BE (web/server/rest_async/rest/collection/models.py)
export const isValidHttpUrl = (url) => {
    try {
        const urlObj = new URL(url);
        return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
    }
    catch {
        return false;
    }
};
export const isValidUrl = (url) => {
    const regex = new RegExp('^((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,})' + // domain name
        "(\\/[\\w\\-._~:/@!$&'()*+,;=%.]*)*" + // path (optional)
        '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string (optional)
        '(\\#[-a-z\\d_]*)?$', // fragment locator (optional)
    'i');
    return !!regex.test(url);
};
export function domainNameFromURL(url) {
    const fullDomain = domainFromURL(url);
    if (fullDomain === 'unknown') {
        return 'unknown';
    }
    // Remove TLD from domain (e.g., "instacart.com" -> "instacart")
    return fullDomain.replace(/\.[^.]*$/, '');
}
export function getFullUrl(url) {
    if (url.startsWith('http://') || url.startsWith('https://')) {
        return url;
    }
    return `https://${url}`;
}
export function getYouTubeID(url) {
    const youtubePattern = [
        // Match youtube.com with various patterns
        '(?:youtube?\\.com\\/',
        // Pattern 1: user/videos/ style paths
        '(?:[^/\\n\\s]+\\/\\S+\\/',
        // Pattern 2: v/ or embed/ or e/ style paths
        '|(?:v|e(?:mbed)?)\\/',
        // Pattern 3: ?v= or &v= style query params
        '|\\S*?[?&]v=',
        ')',
        // OR match youtu.be/ URLs
        '|youtu\\.be\\/',
        // Finally capture the video ID (11 chars)
        ')([a-zA-Z0-9_-]{11})', // Moved capture group outside the alternation
    ].join('');
    const regExp = new RegExp(youtubePattern);
    const match = url.match(regExp);
    if (match && match[1].length == 11) {
        return match[1];
    }
    else {
        return undefined;
    }
}
export const isUnsplashUrl = (url) => {
    return url.indexOf('unsplash.com') > -1;
};
/**
 * Whether or not the URL is coming from Cloudinary.
 *
 * @param url - A URL to test against the expected Cloudinary domain.
 * @returns If the URL matches the Cloudinary domain.
 */
export function isCloudinaryURL(url) {
    return (url.startsWith('https://pplx-res.cloudinary.com/') ||
        url.startsWith('https://api.cloudinary.com/v1_1/pplx/image/download'));
}
/**
 * Checks whether a URL is a patent URL.
 */
export function isPatentUrl(url) {
    const isUsptoUrl = url.startsWith('https://image-ppubs.uspto.gov/dirsearch-public/print/downloadPdf');
    // Check for patent proxy URL - allow any perplexity.ai subdomain or same origin
    // This handles testing.perplexity.ai, www.perplexity.ai, localhost, etc.
    const isProxyUrl = url.includes('/rest/file-repository/patents/') &&
        (isSameOrigin(url) || isPerplexityDomain(url));
    return isUsptoUrl || isProxyUrl;
}
/**
 * Checks if a URL belongs to a perplexity.ai domain (any subdomain).
 */
function isPerplexityDomain(url) {
    try {
        const urlObj = new URL(url);
        return urlObj.hostname === 'perplexity.ai' || urlObj.hostname.endsWith('.perplexity.ai');
    }
    catch {
        return false;
    }
}
/**
 * Whether or not the URL is coming from S3 Code Interpreter Bucket.
 *
 * @param url - A URL to test against the expected S3 bucket.
 * @returns If the URL matches the S3 bucket.
 */
export function isCodeInterpreterChartURL(url) {
    try {
        const hostname = new URL(url).hostname.toLowerCase();
        return (/^ppl-ai-(?:sandbox-)?code-interpreter-files\.s3(?:\.[a-z0-9-]+)?\.amazonaws\.com$/.test(hostname) || url.includes('pplx_code_interpreter'));
    }
    catch {
        return url.includes('pplx_code_interpreter');
    }
}
/**
 * Whether or not the URL is coming from S3 User Generated Assets Bucket.
 *
 * @param url - A URL to test against the expected S3 bucket.
 * @returns If the URL matches the S3 bucket.
 */
export function isUserGeneratedAsset(url) {
    return url.includes('user-gen-media-assets');
}
/**
 * Removes the signature segment from a cloudinary url
 *
 * @param url - A cloudinary url.
 * @returns The cloudinary url with the signature segment removed.
 */
export function cleanCloudinaryUrl(url) {
    return url.replace(/\/s--[^/]+--\/v\d+\//, '/');
}
/**
 * Takes an S3 URL and returns the filename with extension
 */
export const getFilenameFromS3Url = (url) => {
    try {
        const urlObj = new URL(url);
        const pathname = urlObj.pathname.replace(/^\//, '');
        const parts = pathname.split('/');
        const filename = parts[parts.length - 1];
        return decodeURIComponent(filename);
    }
    catch {
        return 'File';
    }
};
export const getFileExtensionFromUrl = (url) => {
    try {
        const filename = new URL(url).pathname.split('/').pop() || '';
        return filename.split('.').pop()?.toLowerCase();
    }
    catch {
        return url.split('.').pop()?.toLowerCase();
    }
};
/**
 * Lowercase extension of a bare filename. Unlike `getFileExtensionFromUrl`,
 * names without one (`README`, dotfiles, trailing dots) yield `undefined`.
 */
export const getFileExtensionFromFilename = (filename) => {
    if (!filename)
        return undefined;
    const lastDotIndex = filename.lastIndexOf('.');
    if (lastDotIndex <= 0 || lastDotIndex === filename.length - 1)
        return undefined;
    return filename.slice(lastDotIndex + 1).toLowerCase();
};
/**
 * Extension of an uploaded attachment. The display name wins so renamed
 * uploads keep their type; a name without an extension ("Q3 Summary") falls
 * back to the URL path (`.../q3.pdf`).
 */
export const getAttachmentFileExtension = (name, url) => {
    const named = getFileExtensionFromFilename(name);
    if (named || !url)
        return named;
    let pathname;
    try {
        pathname = new URL(url).pathname;
    }
    catch {
        pathname = url.split(/[?#]/)[0] ?? '';
    }
    return getFileExtensionFromFilename(pathname.split('/').pop());
};
// Truncate the URL following the logic of x.com
// Essentially, truncate the url to 23 characters unless the domain name is longer.
// If the domain name is longer than 23 characters, allow some context afterwards and then truncate.
export const truncateURL = (url) => {
    const stripped = url;
    const firstSlash = stripped.indexOf('/');
    const length = Math.max(23, firstSlash + 16);
    if (stripped.length > length) {
        return stripped.substring(0, length - 1) + '…';
    }
    else {
        return stripped;
    }
};
// Get a Google Maps directions URL from an address
export const getDirectionsUrl = (placeName, address) => {
    const encodedAddress = encodeURIComponent([placeName, address].filter(Boolean).join(', '));
    return `https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}`;
};
// Get a Google Maps URL from an address
export const getMapsUrl = (placeName, address) => {
    const encodedAddress = encodeURIComponent([placeName, address].filter(Boolean).join(', '));
    return `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
};
/**
 * Interpolates URL template with parameters from given object.
 * Parameters in URL should be in format {paramName}.
 * If parameter is not found in params object, it will be removed from URL.
 *
 * @example
 * interpolateUrl("https://example.com/{domain}/page?email={email}&version=123", { email: 'ff@gm.c' })
 * // returns "https://example.com/page?email=ff@gm.c&version=123"
 */
/**
 * Whether or not the URL is coming from the AI generated image API.
 * Currently supported AI generated APIs:
 * - OpenAI
 *
 * @param url - A URL to test against the expected AI generated image API.
 * @returns If the URL matches the AI generated image API.
 */
/**
 * We cannot open tabs with comet:// scheme in the browser, so we replace it with chrome://
 */
export const replaceCometToChromeScheme = (url) => url.replace('comet://', 'chrome://');
/**
 * Parses url and returns URl object
 * */
export const parseUrl = (url) => {
    if (!url)
        return null;
    try {
        return new URL(url);
    }
    catch {
        return null;
    }
};
/**
 * Checks which URLs from a list are reachable by attempting to load them.
 * This method deliberately only tries to load public URLs, like ones we'd load in an <img>
 * @param urls Array of URLs to check
 * @returns Promise that resolves to an array of reachable URLs
 */
/**
 * Generator version that yields reachable URLs as they are verified, in order of array and in batches
 * The method will begin with batch size 1 and then increase to batchSize once it has found the first reachable URL.
 * This allows us to quickly try to load the first image for display and then slowly load the rest.
 * This method deliberately only tries to load public URLs, like ones we'd load in an <img>
 * @param urls Array of URLs to check
 * @returns Async generator that yields reachable URLs in batches
 */
/**
 * Determines if a given URL is a presigned Amazon S3 URL by checking for common S3 signature query parameters.
 * This method inspects the query string for parameters typically present in presigned S3 URLs.
 * @param url The URL string to check
 * @returns Boolean indicating whether the URL is a presigned S3 URL
 */
export const isPresignedS3Url = (url) => {
    try {
        const urlObj = new URL(url);
        const hasSigV2Params = ['AWSAccessKeyId', 'Signature', 'Expires'].every((key) => urlObj.searchParams.has(key));
        return hasSigV2Params || urlObj.searchParams.has('X-Amz-Signature');
    }
    catch {
        return false;
    }
};
// Matches URLs both as whole strings and embedded in markdown or HTML/JS content, which the
// backend re-signs in place (`streaming_api/signing.py`).
const EMBEDDED_URL_RE = /https?:\/\/[^\s"'`()<>\\]+/g;
const AMZ_DATE_RE = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/;
/**
 * When a presigned URL stops working, in epoch milliseconds: SigV4 `X-Amz-Date` plus
 * `X-Amz-Expires`, or the absolute `Expires` of SigV2 and CloudFront canned policies.
 * Returns undefined for URLs without a readable expiry.
 */
export function getSignedUrlExpiresAt(url) {
    let params;
    try {
        // HTML attributes may escape the query separators.
        params = new URL(url.replace(/&amp;/g, '&')).searchParams;
    }
    catch {
        return undefined;
    }
    const signedAt = params.get('X-Amz-Date');
    const lifetimeSeconds = Number(params.get('X-Amz-Expires'));
    if (signedAt && lifetimeSeconds > 0) {
        if (!AMZ_DATE_RE.test(signedAt))
            return undefined;
        // X-Amz-Date uses the ISO 8601 basic format; Date.parse only accepts the extended one.
        const signedAtMs = Date.parse(signedAt.replace(AMZ_DATE_RE, '$1-$2-$3T$4:$5:$6Z'));
        return Number.isNaN(signedAtMs) ? undefined : signedAtMs + lifetimeSeconds * 1000;
    }
    const expiresSeconds = Number(params.get('Expires'));
    return expiresSeconds > 0 ? expiresSeconds * 1000 : undefined;
}
/**
 * The earliest expiry after `notBefore` among presigned URLs anywhere in `value`: nested
 * objects, arrays, and URLs embedded in string content.
 */
export function findEarliestSignedUrlExpiry(value, notBefore) {
    let earliest;
    const consider = (expiresAt) => {
        if (expiresAt !== undefined && expiresAt > notBefore) {
            earliest = earliest === undefined ? expiresAt : Math.min(earliest, expiresAt);
        }
    };
    if (typeof value === 'string') {
        if (!value.includes('Expires='))
            return undefined;
        for (const [url] of value.matchAll(EMBEDDED_URL_RE))
            consider(getSignedUrlExpiresAt(url));
    }
    else if (typeof value === 'object' && value !== null) {
        for (const child of Array.isArray(value) ? value : Object.values(value)) {
            consider(findEarliestSignedUrlExpiry(child, notBefore));
        }
    }
    return earliest;
}
/**
 * Whether or not the URL is from the same origin as the current window.
 * @param url - The URL to check.
 * @returns True if the URL is from the same origin, else false.
 */
export const isSameOrigin = (url) => {
    try {
        const urlObj = new URL(url);
        return urlObj.origin === window.location.origin;
    }
    catch {
        return false;
    }
};
/**
 * Whether or not the URL is a browser internal link
 * e.g. chrome://extensions, chrome://settings
 * @param url - A URL to test against the browser internal URL
 * @returns If the URL is the browser internal URL
 */
export function isBrowserInternalUrl(url) {
    return (url.startsWith('chrome://') ||
        url.startsWith('chrome-extension://') ||
        url.startsWith('browser-extension://') ||
        url.startsWith('browser://') ||
        url.startsWith('comet://'));
}
export function domainFromURL(url) {
    // Regex to handle:
    // 1. Traditional domains with TLD (example.com)
    // 2. localhost (excluding port)
    // 3. IP addresses (excluding port)
    const domainRegex = /(?:https?:\/\/)?(?:www\.)?([a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*(?:\.[a-zA-Z]{2,})?|localhost|\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})(?::\d+)?/;
    const matches = url.match(domainRegex);
    if (matches && matches.length > 0) {
        return matches[1] || matches[0];
    }
    return 'unknown';
}
export function getFaviconUrl(url) {
    // get domain or subdomain for favicon retrieval
    // return subdomain for gmail/gcal sites
    if (url.includes('calendar.google.com') || url.includes('google.com/calendar')) {
        return 'https://calendar.google.com';
    }
    if (url.includes('mail.google.com') || url.includes('google.com/mail')) {
        return 'https://mail.google.com';
    }
    // For Comet internal pages we want to show Perplexity logo
    if (isBrowserInternalUrl(url)) {
        return 'www.perplexity.ai';
    }
    return domainFromURL(url);
}
/**
 * Removes the query string from a URL, returning only the base URL.
 * This method returns the portion of the string before the first '?' character, or the original string if no query string is present.
 * @param str The URL string to process
 * @returns The URL string without any query parameters
 */
export const removeQueryString = (str) => {
    if (!str)
        return '';
    const index = str.indexOf('?');
    const withoutQuery = index === -1 ? str : str.substring(0, index);
    return withoutQuery.replace(/\/+$/, ''); // Remove trailing slashes
};
/** Drops the signature from a presigned S3 URL so it matches the bare upload URL. */
export const withoutPresignedQuery = (url) => isPresignedS3Url(url) ? removeQueryString(url) : url;
/**
 * Get the favicon URL from google for a domain
 */
export const getFaviconForUrl = (url, size = 32) => {
    return `https://www.google.com/s2/favicons?sz=${size}&domain=${url}`;
};
/**
 * Check if a URL is downloadable (local S3, cloudinary, or patent URL).
 * This is the core logic for determining downloadability based on URL pattern.
 */
export const isDownloadableUrl = (url) => {
    // Legacy image uploads use cloudinary. They have all expired but we
    // should still attempt to download them and show an error toast.
    return isLocalFile(url) || isCloudinaryURL(url) || isPatentUrl(url);
};
export function getImageDownloadFilename(url, filename) {
    let pathname;
    try {
        pathname = new URL(url).pathname;
    }
    catch {
        return filename;
    }
    if (!getMimeTypeFromUrl(pathname).startsWith('image/'))
        return filename;
    const extension = pathname.slice(pathname.lastIndexOf('.')).toLowerCase();
    const stem = getMimeTypeFromUrl(filename).startsWith('image/')
        ? filename.slice(0, filename.lastIndexOf('.'))
        : filename;
    return `${stem}${extension}`;
}
export const getMimeTypeFromUrl = (url) => {
    // Special case for patent URLs - these are PDFs
    if (isPatentUrl(url))
        return 'application/pdf';
    // Remove query string and fragment
    const cleanUrl = url.split('?')[0].split('#')[0];
    const extension = cleanUrl.split('.').pop()?.toLowerCase();
    if (!extension)
        return 'application/octet-stream';
    const mimeTypes = {
        // Images
        png: 'image/png',
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        jpe: 'image/jpeg',
        jp2: 'image/jp2',
        gif: 'image/gif',
        bmp: 'image/bmp',
        tiff: 'image/tiff',
        tif: 'image/tiff',
        svg: 'image/svg+xml',
        webp: 'image/webp',
        ico: 'image/x-icon',
        avif: 'image/avif',
        heic: 'image/heic',
        heif: 'image/heif',
        // Videos
        mp4: 'video/mp4',
        webm: 'video/webm',
        mov: 'video/quicktime',
        avi: 'video/x-msvideo',
        mkv: 'video/x-matroska',
        // Documents
        pdf: 'application/pdf', // PDF
        doc: 'application/msword', // DOC
        docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // DOCX
        txt: 'text/plain', // TXT
        csv: 'text/csv', // CSV
        xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // XLSX
        pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation', // PPTX
        md: 'text/markdown', // MARKDOWN
    };
    return mimeTypes[extension] || 'application/octet-stream'; // Default to application/octet-stream if no match
};
/**
 * Converts any URL string (absolute or relative) into a standardized URL object.
 * Handles both absolute URLs (with protocol) and relative URLs by using the current
 * page location as a base when necessary.
 *
 * @param url - URL string to sanitize (can be absolute or relative)
 * @returns URL object with parsed components
 *
 * @example
 * sanitizeUrl("https://example.com/path")
 * // returns URL { href: "https://example.com/path", ... }
 *
 * sanitizeUrl("/relative/path")
 * // returns URL { href: "https://current-domain.com/relative/path", ... }
 */
export function sanitizeUrl(url) {
    let sanitizedUrl;
    try {
        sanitizedUrl = new URL(url);
    }
    catch {
        const base = window.location.origin || window.location.href;
        sanitizedUrl = new URL(url, base);
    }
    return sanitizedUrl;
}
export const isLocalFile = (url) => {
    return FILE_UPLOAD_BUCKET_NAMES.some((bucketName) => url.includes(`${bucketName}.s3.amazonaws.com/web/direct-files`));
};
//# sourceMappingURL=urls.js.map